/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Chips de propina en cobro de mesa / comer acá. No es tip de rider.
 */
import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatPosMoney } from '../../application/posMoneyFormat';
import {
  POS_TIP_SUGGESTED_PERCENTS,
  tipAmountFromPercent,
  type PosTipIntent,
} from '../../application/posCheckoutTip';

type Props = {
  merchandiseTotal: number;
  tipIntent: PosTipIntent | null;
  onSelect: (next: PosTipIntent | null) => void;
};

export function PosCheckoutTipChips({ merchandiseTotal, tipIntent, onSelect }: Props) {
  const selectedPercent = tipIntent?.percent ?? null;
  const selectedAmount = tipIntent?.amount ?? 0;
  const noneSelected = !tipIntent || selectedAmount <= 0;

  const chips = useMemo(
    () =>
      POS_TIP_SUGGESTED_PERCENTS.map((percent) => ({
        percent,
        amount: tipAmountFromPercent(merchandiseTotal, percent),
      })),
    [merchandiseTotal],
  );

  return (
    <View style={styles.wrap} testID="pos-checkout-tip-chips">
      <Text style={styles.title}>Propina</Text>
      <Text style={styles.hint}>Se suma al total a cobrar.</Text>
      <View style={styles.row}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityState={{ selected: noneSelected }}
          accessibilityLabel="Sin propina"
          testID="pos-checkout-tip-none"
          style={[styles.chip, noneSelected && styles.chipOn]}
          onPress={() => onSelect({ amount: 0, percent: null, source: 'staff' })}
        >
          <Text style={[styles.chipText, noneSelected && styles.chipTextOn]}>Sin propina</Text>
        </TouchableOpacity>
        {chips.map((chip) => {
          const selected =
            !noneSelected &&
            selectedPercent === chip.percent &&
            Math.abs(selectedAmount - chip.amount) < 0.01;
          return (
            <TouchableOpacity
              key={chip.percent}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`Propina ${chip.percent} por ciento`}
              testID={`pos-checkout-tip-${chip.percent}`}
              style={[styles.chip, selected && styles.chipOn]}
              onPress={() =>
                onSelect({
                  amount: chip.amount,
                  percent: chip.percent,
                  source: 'staff',
                })
              }
            >
              <Text style={[styles.chipText, selected && styles.chipTextOn]}>{chip.percent}%</Text>
              {chip.amount > 0 ? (
                <Text style={[styles.chipSub, selected && styles.chipSubOn]}>
                  {formatPosMoney(chip.amount)}
                </Text>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
      {selectedAmount > 0 ? (
        <Text style={styles.selectedHint}>
          Propina {formatPosMoney(selectedAmount)}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: 8,
    marginBottom: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  hint: {
    fontSize: 12,
    color: ticketColors.ink3,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    minHeight: 44,
    minWidth: 72,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipOn: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  chipTextOn: {
    color: ticketColors.goldInk,
  },
  chipSub: {
    marginTop: 2,
    fontSize: 10,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  chipSubOn: {
    color: ticketColors.goldInk,
    opacity: 0.9,
  },
  selectedHint: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
});
